import { useCallback, useEffect, useState } from "react";
import { HardDrive, Loader2, FileText, Plus, Trash2, FileStack } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const mb = (bytes) => `${(bytes / 1048576).toFixed(1)} MB`;

export default function AdminDocuments() {
  const [d, setD] = useState(null);
  const [tpls, setTpls] = useState([]);
  const [denied, setDenied] = useState(false);
  const [settings, setSettings] = useState(null);
  const [tpl, setTpl] = useState({ name: "", category: "General", description: "" });

  const load = useCallback(async () => {
    try {
      const [o, t] = await Promise.all([api.get("/admin/documents/overview"), api.get("/admin/templates")]);
      setD(o.data); setSettings(o.data.settings); setTpls(t.data.templates);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      await api.put("/admin/documents/settings", {
        limit_mb_free: settings.limit_mb_free, limit_mb_paid: settings.limit_mb_paid,
        allowed_types: typeof settings.allowed_types === "string"
          ? settings.allowed_types.split(",").map((s) => s.trim()).filter(Boolean) : settings.allowed_types,
      });
      toast.success("Storage settings saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createTpl = async () => {
    try { await api.post("/admin/templates", tpl); toast.success("Template created"); setTpl({ name: "", category: "General", description: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTpl = async (id) => { try { await api.delete(`/admin/templates/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  if (denied) return <p className="text-sm text-[#71717A]" data-testid="admin-documents-denied">You don't have the "content.manage" permission.</p>;
  if (!d) return <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const allowedStr = Array.isArray(settings.allowed_types) ? settings.allowed_types.join(", ") : settings.allowed_types;

  return (
    <div data-testid="admin-documents" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="doc-total">
          <div className="w-9 h-9 rounded-lg fv-gold-gradient flex items-center justify-center mb-3"><FileText className="w-4 h-4 text-[#0A0A0B]" /></div>
          <div className="font-heading text-2xl text-white">{d.total_documents.toLocaleString()}</div>
          <div className="text-[#71717A] text-sm">Documents stored</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="doc-storage">
          <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-3"><HardDrive className="w-4 h-4 text-[#D4AF37]" /></div>
          <div className="font-heading text-2xl text-white">{mb(d.total_bytes)}</div>
          <div className="text-[#71717A] text-sm">Total storage used</div>
        </div>
      </div>

      <div>
        <h3 className="text-white font-medium mb-3">Storage by category</h3>
        <div className="rounded-2xl border border-white/8 bg-[#121214] divide-y divide-white/5" data-testid="doc-by-category">
          {d.by_category.map((c) => (
            <div key={c.category} className="flex items-center justify-between px-5 py-3">
              <span className="text-[#D4D4D8]">{c.category}</span>
              <span className="text-[#71717A] text-sm">{c.count} files · {mb(c.bytes)}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-white font-medium mb-3">Storage & file settings</h3>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5 space-y-4" data-testid="doc-settings">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-[#71717A]">Storage limit — Free (MB)</label>
              <Input type="number" value={settings.limit_mb_free} onChange={(e) => setSettings({ ...settings, limit_mb_free: parseInt(e.target.value, 10) || 0 })} data-testid="doc-limit-free" className="bg-[#0d0d0f] border-white/10 text-white mt-1" /></div>
            <div><label className="text-xs text-[#71717A]">Storage limit — Paid (MB)</label>
              <Input type="number" value={settings.limit_mb_paid} onChange={(e) => setSettings({ ...settings, limit_mb_paid: parseInt(e.target.value, 10) || 0 })} data-testid="doc-limit-paid" className="bg-[#0d0d0f] border-white/10 text-white mt-1" /></div>
          </div>
          <div><label className="text-xs text-[#71717A]">Allowed file types (comma-separated)</label>
            <Input value={allowedStr} onChange={(e) => setSettings({ ...settings, allowed_types: e.target.value })} data-testid="doc-allowed-types" className="bg-[#0d0d0f] border-white/10 text-white mt-1" /></div>
          <Button onClick={saveSettings} data-testid="doc-save-settings" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Save settings</Button>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3"><FileStack className="w-5 h-5 text-[#D4AF37]" /><h3 className="text-white font-medium">Global templates</h3></div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5 mb-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input placeholder="Template name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} data-testid="tpl-name" className="bg-[#0d0d0f] border-white/10 text-white flex-1" />
            <Input placeholder="Category" value={tpl.category} onChange={(e) => setTpl({ ...tpl, category: e.target.value })} data-testid="tpl-category" className="bg-[#0d0d0f] border-white/10 text-white sm:w-40" />
            <Button onClick={createTpl} data-testid="tpl-create" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
        </div>
        <div className="space-y-2" data-testid="tpl-list">
          {tpls.length ? tpls.map((t) => (
            <div key={t.template_id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#121214] px-5 py-3">
              <div><span className="text-white">{t.name}</span><span className="ml-2 text-xs text-[#D4AF37]">{t.category}</span>
                {t.description && <span className="ml-2 text-xs text-[#71717A]">{t.description}</span>}</div>
              <button onClick={() => delTpl(t.template_id)} data-testid={`tpl-del-${t.template_id}`} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          )) : <p className="text-sm text-[#71717A]">No global templates yet.</p>}
        </div>
      </div>
    </div>
  );
}
